import { CANVAS_WIDTH, CANVAS_HEIGHT } from "../../game/constants";
import { easeOutCubic } from "../../shared/easing";
import { GEM_PACKS } from "../../economy/characters";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { drawButton } from "../ui";
import {
  GAME_FONT,
  mFont,
  mFontPlain,
  menuButtonColumns,
  menuBtnH,
  menuBtnHLg,
  menuGap,
  roundedRectPath,
  MENU_TEXT_PRIMARY,
  MENU_TEXT_SECONDARY,
  UI_EDGE_PAD,
  UI_CLOSE_Y,
  uiCloseSize,
} from "../renderConstants";

// ── Types ────────────────────────────────────────────────────────────────

/** Mutable rects written by the gem shop for header icon hotspots. */
export interface GemShopMutableRefs {
  headerCoinIconRect: Rect | null;
  headerGemIconRect: Rect | null;
  gemPackImageRects: (Rect | null)[];
}

export interface GemShopDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowMs: () => number;
  gemPackImages: (HTMLImageElement | null)[];
  /** Mutable refs — written to during draw. */
  mutableRefs: GemShopMutableRefs;
  /** Helper callbacks forwarded from the renderer class. */
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawCircleCloseButton: (rect: Rect) => void;
  drawCoinIcon: (x: number, y: number, size: number) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
  measureTextWidth: (text: string) => number;
  staggerAlpha: (index: number, delayMs: number, durationMs: number) => number;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawGemShopScreen(deps: GemShopDeps, model: RenderModel): UiRects {
  const ctx = deps.ctx;
  const centerX = CANVAS_WIDTH / 2;
  const menuCols = menuButtonColumns(centerX);
  const uiRects: UiRects = {};
  const g = menuGap();

  // Close button (top-right)
  const closeRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  deps.drawCircleCloseButton(closeRect);
  uiRects.gemShopBack = closeRect;

  // Title
  const titleY = UI_CLOSE_Y + uiCloseSize() / 2;
  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 10px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("BUY GEMS", centerX, titleY);

  // Coin + Gem balance row
  const balanceY = titleY + 16;
  const balIconSz = 10;
  const balGap = 12;
  ctx.font = mFont("bold", 6);
  const coinStr = `${model.coins}`;
  const gemStr = `${model.gems}`;
  const coinTextW = deps.measureTextWidth(coinStr);
  const gemTextW = deps.measureTextWidth(gemStr);
  const totalBalW = balIconSz + 3 + coinTextW + balGap + balIconSz + 3 + gemTextW;
  const balStartX = centerX - totalBalW / 2;

  // Coins
  deps.drawCoinIcon(balStartX, balanceY - balIconSz / 2, balIconSz);
  ctx.fillStyle = "#ffd700";
  ctx.font = mFont("bold", 6);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(coinStr, balStartX + balIconSz + 3, balanceY);

  // Gems
  const gemIconX = balStartX + balIconSz + 3 + coinTextW + balGap;
  deps.drawGemIcon(gemIconX, balanceY - balIconSz / 2, balIconSz);
  deps.mutableRefs.headerGemIconRect = { x: gemIconX, y: balanceY - balIconSz / 2, w: balIconSz, h: balIconSz };
  deps.mutableRefs.headerCoinIconRect = { x: balStartX, y: balanceY - balIconSz / 2, w: balIconSz, h: balIconSz };
  ctx.fillStyle = "#64d8ff";
  ctx.font = mFont("bold", 6);
  ctx.fillText(gemStr, gemIconX + balIconSz + 3, balanceY);

  // --- 2×2 grid (label → image → buttons side-by-side) ---
  const cols = 2;
  const rows = 2;
  const gridW = CANVAS_WIDTH - UI_EDGE_PAD * 2;
  const gapX = g;
  const gapY = g + 8;
  const cardW = Math.floor((gridW - gapX * (cols - 1)) / cols);
  const btnH = menuBtnH();
  const maxImgH = Math.floor(cardW * 0.5);
  const labelH = 12;
  const btnGap = 4;
  const halfBtnW = Math.floor((cardW - 12 - btnGap) / 2);
  const cardH = labelH + 4 + maxImgH + 6 + btnH;
  const gridX = UI_EDGE_PAD;
  const totalGridH = rows * cardH + (rows - 1) * gapY;
  const canvasH = deps.canvasHeight;
  const gridTopY = Math.max(balanceY + 14, (canvasH - totalGridH) / 2 - 10);

  for (let i = 0; i < GEM_PACKS.length; i++) {
    const pack = GEM_PACKS[i];
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = gridX + col * (cardW + gapX);
    const cy = gridTopY + row * (cardH + gapY);
    const canAfford = model.coins >= pack.coinCost;

    // Stagger-in animation
    const gridAlpha = deps.staggerAlpha(i, 60, 250);
    if (gridAlpha <= 0) continue;
    const hasAnim = gridAlpha < 1;
    if (hasAnim) {
      ctx.save();
      const scl = 0.8 + 0.2 * easeOutCubic(gridAlpha);
      const cardCX = cx + cardW / 2;
      const cardCY = cy + cardH / 2;
      ctx.globalAlpha = easeOutCubic(gridAlpha);
      ctx.translate(cardCX, cardCY);
      ctx.scale(scl, scl);
      ctx.translate(-cardCX, -cardCY);
    }

    // Label above image (white)
    ctx.fillStyle = "#ffffff";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(pack.label, cx + cardW / 2, cy);

    // Floating image — preserve aspect ratio, fit within maxImgH
    const imgTopY = cy + labelH + 4;
    const packImg = deps.gemPackImages[i];
    if (packImg != null) {
      const natW = packImg.naturalWidth || 1;
      const natH = packImg.naturalHeight || 1;
      const aspect = natW / natH;
      let drawW: number, drawH: number;
      if (aspect >= 1) {
        drawW = Math.min(cardW - 12, maxImgH * aspect);
        drawH = drawW / aspect;
      } else {
        drawH = maxImgH;
        drawW = drawH * aspect;
      }
      if (drawW > cardW - 12) { drawW = cardW - 12; drawH = drawW / aspect; }
      // Scale down the 5-gem pack (index 0) — its image is larger relative to others
      if (i === 0) { drawW *= 0.5; drawH *= 0.5; }
      const imgX = cx + (cardW - drawW) / 2;
      const imgY = imgTopY + (maxImgH - drawH) / 2;
      ctx.drawImage(packImg, imgX, imgY, drawW, drawH);
      deps.mutableRefs.gemPackImageRects[i] = { x: imgX, y: imgY, w: drawW, h: drawH };
    }

    // --- Two buttons side by side ---
    const btnX = cx + 6;
    const btnsY = imgTopY + maxImgH + 6;

    // Left: Coin buy button
    const coinBtnRect: Rect = { x: btnX, y: btnsY, w: halfBtnW, h: btnH };
    deps.drawBtn(coinBtnRect, "", {
      fill: canAfford ? "rgba(20,36,54,0.85)" : "rgba(15,15,25,0.8)",
      stroke: canAfford ? "rgba(108,200,255,0.6)" : "rgba(68,68,68,0.4)",
      variant: "lobby",
    });

    // Coin icon + cost (full number, no K)
    const costStr = `${pack.coinCost}`;
    ctx.font = mFont("bold", 5);
    const costTextW = deps.measureTextWidth(costStr);
    const coinIconSz = 8;
    const totalCostW = coinIconSz + 2 + costTextW;
    const costStartX = btnX + (halfBtnW - totalCostW) / 2;

    deps.drawCoinIcon(costStartX, btnsY + (btnH - coinIconSz) / 2, coinIconSz);
    ctx.fillStyle = canAfford ? "#ffd700" : "#555";
    ctx.font = mFont("bold", 5);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(costStr, costStartX + coinIconSz + 2, btnsY + btnH / 2);

    // Right: SOL buy button
    const solBtnX = btnX + halfBtnW + btnGap;
    const solBtnRect: Rect = { x: solBtnX, y: btnsY, w: halfBtnW, h: btnH };

    const solItem = model.tokenShopItems.find(t => t.gemAmount === pack.gems);
    const hasSolPrice = solItem != null && solItem.tokenPrice != null;

    deps.drawBtn(solBtnRect, "", {
      fill: hasSolPrice ? "rgba(5,30,20,0.85)" : "rgba(15,15,25,0.8)",
      stroke: hasSolPrice ? "rgba(20,241,149,0.6)" : "rgba(68,68,68,0.4)",
      variant: "lobby",
    });

    // SOL price text
    const solSymbol = model.activeTokenSymbol ?? "SOL";
    ctx.font = mFont("bold", 5);
    ctx.textBaseline = "middle";
    if (hasSolPrice) {
      const solPriceStr = solItem!.tokenPrice!.toFixed(4);
      ctx.fillStyle = "#14f195";
      ctx.textAlign = "center";
      ctx.fillText(solPriceStr, solBtnX + halfBtnW / 2, btnsY + btnH / 2);
    } else {
      ctx.fillStyle = "#555";
      ctx.textAlign = "center";
      ctx.fillText(`$${solSymbol}`, solBtnX + halfBtnW / 2, btnsY + btnH / 2);
    }

    if (hasAnim) ctx.restore();

    // Register hit rects
    const coinKey = `shopBuyGems${i}` as keyof UiRects;
    (uiRects as Record<string, Rect>)[coinKey] = coinBtnRect;
    const solKey = `shopBuySol${i}` as keyof UiRects;
    (uiRects as Record<string, Rect>)[solKey] = solBtnRect;
  }

  // --- IAP Gem Packs (native only) ---
  const gemGridBottomY = gridTopY + totalGridH;

  if (model.iapAvailable && model.iapPacks.length > 0) {
    const sectionGapY = g + 8;
    const gemStartX = menuCols.fullX;
    const gemPackW = menuCols.fullW;
    const iapStartY = gemGridBottomY + sectionGapY;

    // Section divider
    ctx.fillStyle = "#555";
    ctx.fillRect(gemStartX + 20, iapStartY - 4, gemPackW - 40, 1);

    // Section header
    ctx.fillStyle = MENU_TEXT_PRIMARY;
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("PREMIUM GEM PACKS", centerX, iapStartY + 2);

    const iapPackStartY = iapStartY + 18;
    const iapPackH = menuBtnHLg();
    const iapGapY = menuGap();

    for (let i = 0; i < model.iapPacks.length; i++) {
      const pack = model.iapPacks[i];
      const py = iapPackStartY + i * (iapPackH + iapGapY);

      const iapPackRect: Rect = { x: gemStartX, y: py, w: gemPackW, h: iapPackH };
      deps.drawBtn(iapPackRect, "", {
        fill: pack.bestValue ? "rgba(45,25,60,0.85)" : "rgba(20,36,54,0.85)",
        stroke: pack.bestValue ? "rgba(180,74,255,0.6)" : "rgba(108,200,255,0.6)",
        variant: "lobby",
      });

      if (pack.bestValue) {
        const badgeText = "BEST VALUE";
        ctx.font = mFont("bold", 4);
        const badgeW = deps.measureTextWidth(badgeText) + 8;
        const badgeH = 12;
        const badgeX = gemStartX + gemPackW - badgeW - 4;
        const badgeY = py + 2;
        const badgePulse = 1 + 0.06 * Math.sin(deps.nowMs() / 400);
        ctx.save();
        const bcx = badgeX + badgeW / 2;
        const bcy = badgeY + badgeH / 2;
        ctx.translate(bcx, bcy);
        ctx.scale(badgePulse, badgePulse);
        ctx.translate(-bcx, -bcy);
        roundedRectPath(ctx, badgeX, badgeY, badgeW, badgeH, 3);
        ctx.fillStyle = "#b44aff";
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(badgeText, bcx, bcy);
        ctx.restore();
      }

      deps.drawGemIcon(gemStartX + 8, py + (iapPackH - 14) / 2, 14);
      ctx.fillStyle = pack.bestValue ? "#d8aaff" : "#64d8ff";
      ctx.font = mFont("bold", 7);
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(pack.label, gemStartX + 28, py + iapPackH / 2);

      ctx.fillStyle = "#6bff6b";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(pack.priceString, gemStartX + gemPackW - 8, py + iapPackH / 2);
      ctx.textAlign = "center";

      const iapKey = `shopBuyIap${i}` as keyof UiRects;
      (uiRects as Record<string, Rect>)[iapKey] = { x: gemStartX, y: py, w: gemPackW, h: iapPackH };
    }

    // Restore Purchases button
    const restoreY = iapPackStartY + model.iapPacks.length * (iapPackH + iapGapY) + 4;
    ctx.fillStyle = "#888";
    ctx.font = mFontPlain(5);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("Restore Purchases", centerX, restoreY);
    const restoreTextW = deps.measureTextWidth("Restore Purchases");
    uiRects.shopRestoreIap = {
      x: centerX - restoreTextW / 2 - 8,
      y: restoreY - 2,
      w: restoreTextW + 16,
      h: 16,
    };
  }

  // --- IAP Purchase flow overlays ---
  const ps = model.iapPurchaseState;

  if (ps.phase === "confirm") {
    // Dim overlay behind dialog
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Confirmation dialog card
    const dialogW = menuCols.fullW - 16;
    const dialogH = 120;
    const dialogX = centerX - dialogW / 2;
    const dialogY = CANVAS_HEIGHT / 2 - dialogH / 2;

    roundedRectPath(ctx, dialogX, dialogY, dialogW, dialogH, 10);
    ctx.fillStyle = "rgba(15,20,40,0.98)";
    ctx.fill();
    roundedRectPath(ctx, dialogX, dialogY, dialogW, dialogH, 10);
    ctx.strokeStyle = "#64d8ff";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Title
    ctx.fillStyle = MENU_TEXT_PRIMARY;
    ctx.font = mFont("bold", 8);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("Confirm Purchase", centerX, dialogY + 12);

    // Pack details
    ctx.fillStyle = "#64d8ff";
    ctx.font = mFont("bold", 7);
    ctx.fillText(ps.label, centerX, dialogY + 30);

    ctx.fillStyle = "#6bff6b";
    ctx.font = mFont("bold", 9);
    ctx.fillText(ps.priceString, centerX, dialogY + 48);

    // Buttons
    const btnW = (dialogW - 24) / 2;
    const confirmBtnH = 30;
    const btnY = dialogY + dialogH - confirmBtnH - 12;

    const cancelRect: Rect = { x: dialogX + 8, y: btnY, w: btnW, h: confirmBtnH };
    deps.drawBtn(cancelRect, "CANCEL", { fill: "rgba(42,26,26,0.85)", stroke: "rgba(255,107,107,0.6)", font: mFont("bold", 6), variant: "lobby" });
    uiRects.shopIapCancel = cancelRect;

    const confirmRect: Rect = { x: dialogX + 8 + btnW + 8, y: btnY, w: btnW, h: confirmBtnH };
    deps.drawBtn(confirmRect, "BUY NOW", { fill: "rgba(26,58,26,0.85)", stroke: "rgba(107,255,107,0.6)", font: mFont("bold", 6), variant: "lobby" });
    uiRects.shopIapConfirm = confirmRect;
  }

  if (ps.phase === "loading") {
    // Dim overlay
    ctx.fillStyle = "rgba(0,0,0,0.7)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Loading card
    const loadW = 160;
    const loadH = 80;
    const loadX = centerX - loadW / 2;
    const loadY = CANVAS_HEIGHT / 2 - loadH / 2;

    roundedRectPath(ctx, loadX, loadY, loadW, loadH, 10);
    ctx.fillStyle = "rgba(15,20,40,0.98)";
    ctx.fill();
    roundedRectPath(ctx, loadX, loadY, loadW, loadH, 10);
    ctx.strokeStyle = "#64d8ff";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Hourglass animation
    deps.drawHourglass(centerX, loadY + 30, 28, "#64d8ff");

    ctx.fillStyle = MENU_TEXT_SECONDARY;
    ctx.font = mFontPlain(6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("Processing purchase...", centerX, loadY + 52);
  }

  if (ps.phase === "success") {
    // Success toast at top of gem shop area
    const toastW = menuCols.fullW - 16;
    const toastH = 40;
    const toastX = centerX - toastW / 2;
    const toastY = 130;

    // Fade based on remaining timer (fade out last 0.5s)
    const alpha = Math.min(1, ps.messageTimer / 0.5);
    ctx.globalAlpha = alpha;

    roundedRectPath(ctx, toastX, toastY, toastW, toastH, 8);
    ctx.fillStyle = "rgba(20,60,20,0.95)";
    ctx.fill();
    roundedRectPath(ctx, toastX, toastY, toastW, toastH, 8);
    ctx.strokeStyle = "#6bff6b";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#6bff6b";
    ctx.font = mFont("bold", 8);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(ps.message, centerX, toastY + toastH / 2);

    ctx.globalAlpha = 1;

    // Tap anywhere to dismiss
    uiRects.shopIapDismiss = { x: 0, y: 120, w: CANVAS_WIDTH, h: CANVAS_HEIGHT - 120 };
  }

  if (ps.phase === "error") {
    // Error toast at top of gem shop area
    const toastW = menuCols.fullW - 16;
    const toastH = 40;
    const toastX = centerX - toastW / 2;
    const toastY = 130;

    const alpha = Math.min(1, ps.messageTimer / 0.5);
    ctx.globalAlpha = alpha;

    roundedRectPath(ctx, toastX, toastY, toastW, toastH, 8);
    ctx.fillStyle = "rgba(60,20,20,0.95)";
    ctx.fill();
    roundedRectPath(ctx, toastX, toastY, toastW, toastH, 8);
    ctx.strokeStyle = "#ff6b6b";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#ff6b6b";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(ps.message, centerX, toastY + toastH / 2);

    ctx.globalAlpha = 1;

    // Tap anywhere to dismiss
    uiRects.shopIapDismiss = { x: 0, y: 120, w: CANVAS_WIDTH, h: CANVAS_HEIGHT - 120 };
  }

  return uiRects;
}
